import React, { useState } from 'react';
import { Briefcase, Calendar, TrendingUp, Bell, Shield, CheckCircle, Clock, FileText, Users, ChevronDown, BarChart3, Globe } from 'lucide-react';
import CTASection from '../../components/CTASection';
import RegistrationModal from '../../components/RegistrationModal';
import ServicePricing from '../../components/ServicePricing';
import SEO from '../../components/SEO';

const PortfolioManagementPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);

  const benefits = [
    { icon: Calendar, title: 'Never Miss a Deadline', description: 'Automated tracking and reminders for every renewal, maintenance filing, and statutory deadline across your entire portfolio.' },
    { icon: Shield, title: 'Protect Every Asset', description: 'Comprehensive monitoring ensures no trademark lapses or becomes vulnerable to cancellation due to missed filings.' },
    { icon: TrendingUp, title: 'Strategic Growth Planning', description: 'Identify portfolio gaps, expansion opportunities, and consolidation strategies to maximize brand protection ROI.' },
    { icon: Globe, title: 'Global Coverage', description: 'Manage trademarks across multiple countries and jurisdictions from one centralized platform with expert local counsel.' },
  ];

  const processSteps = [
    { title: 'Portfolio Audit', description: 'We conduct a comprehensive review of your existing trademarks, identifying strengths, gaps, and upcoming deadlines across all jurisdictions.' },
    { title: 'Strategic Planning', description: 'Our attorneys develop a tailored management plan including renewal schedules, expansion recommendations, and risk mitigation strategies.' },
    { title: 'Ongoing Management', description: 'We handle all filings, renewals, and monitoring while providing you with regular reports and strategic updates via your dashboard.' },
  ];

  const faqs = [
    { question: 'How many trademarks can you manage in my portfolio?', answer: 'There is no limit. We manage portfolios ranging from a handful of marks to thousands across multiple jurisdictions. Our systems and team scale to match your needs, whether you have 5 trademarks or 5,000.' },
    { question: 'What happens if a renewal deadline is approaching?', answer: 'You will receive automated alerts at 6 months, 3 months, and 1 month before any deadline. Our team proactively prepares all necessary filings and coordinates with you for approval well in advance.' },
    { question: 'Can you manage trademarks filed through other firms?', answer: 'Absolutely. We can take over management of trademarks originally filed by other attorneys or firms. We will conduct a full audit, update all records, and ensure seamless ongoing management.' },
    { question: 'What reports do I receive?', answer: 'You receive monthly portfolio status reports, quarterly strategic reviews, and annual comprehensive audits. All reports include deadline summaries, filing statuses, risk assessments, and strategic recommendations.' },
    { question: 'Do you handle international trademark management?', answer: 'Yes. We manage trademarks in over 180 countries through our network of local counsel. This includes Madrid Protocol filings, individual country registrations, and coordinated global brand protection strategies.' },
    { question: 'What is included in the portfolio audit?', answer: 'Our audit covers: verification of all active registrations, identification of upcoming deadlines, gap analysis for unprotected marks or classes, risk assessment for potential conflicts, and strategic recommendations for portfolio optimization.' },
  ];

  return (
    <div>
      <SEO 
        title="Trademark Portfolio Management | Norfolk Trademark"
        description="Manage, monitor, and protect your trademark portfolio with expert IP management services."
        keywords="trademark portfolio, IP management, trademark monitoring, brand portfolio"
        canonical="/services/portfolio-management"
      />
      {/* Hero */}
      <div className="relative text-white py-20 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Portfolio Management
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Trademark <span className="text-[#FF035B]">Portfolio Management</span>
            </h1>
            <p className="text-base text-gray-300 mb-8">
              Comprehensive management of your entire trademark portfolio with deadline tracking, renewals, and strategic planning — so you never lose a mark to missed filings.
            </p>
            <div className="flex gap-4">
              <button onClick={openModal} className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white font-bold px-8 py-3 rounded-lg transition-all">
                Get Portfolio Review
              </button>
              <button onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-3 rounded-lg transition-all">
                Speak with an Expert
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-[#FF035B]" />180+ Countries</span>
              <span className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#FF035B]" />24/7 Monitoring</span>
              <span className="flex items-center gap-2"><Shield className="w-5 h-5 text-[#FF035B]" />Expert Attorneys</span>
            </div>
          </div>
        </div>
      </div>

      {/* What Is Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is Portfolio Management?
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Centralize, Monitor & Grow Your Brand Assets</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Managing multiple trademarks across jurisdictions requires constant attention to deadlines, renewals, and maintenance filings. A single missed deadline can result in the cancellation of a valuable trademark, potentially costing your business millions.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Our portfolio management service provides a centralized dashboard where every trademark, deadline, and filing is tracked in real time. We handle Section 8 declarations, Section 15 incontestability filings, renewals, and all maintenance requirements.
              </p>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Beyond maintenance, we provide strategic analysis — identifying gaps in your protection, recommending new filings for expanding product lines, and ensuring your brand strategy aligns with your business growth plans.
              </p>
              <p className="text-base font-semibold text-black">
                Whether you have 5 trademarks or 500, our team ensures every asset is protected, maintained, and strategically managed.
              </p>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80" alt="Portfolio dashboard" className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">Why Portfolio Management?</div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Protect Your Entire Brand Ecosystem</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">Don't let valuable trademarks lapse due to missed deadlines or lack of strategic oversight.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group">
                <div className="w-14 h-14 bg-[#FF035B]/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <b.icon className="w-7 h-7 text-[#FF035B]" />
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Process Steps */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">Our Process</div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">How We Manage Your Portfolio</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">A streamlined process to take full control of your trademark assets.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                <div className="w-12 h-12 bg-[#FF035B] rounded-full flex items-center justify-center text-white font-bold text-lg mb-5">
                  {i + 1}
                </div>
                <h3 className="text-lg font-bold text-black mb-3">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <ServicePricing showPrices={true} title="Portfolio Management Packages" subtitle="Comprehensive trademark portfolio protection" />

      {/* FAQ */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-[#FF035B]/10 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">FAQ</div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">Everything you need to know about trademark portfolio management.</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-black pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FF035B] flex-shrink-0 transition-transform ${openFAQ === i ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default PortfolioManagementPage;
